import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import {Swiper, SwiperSlide} from 'swiper/react';
import {Keyboard} from 'swiper';
import 'swiper/css';
import Vimeo from "@u-wave/react-vimeo";
import {CSSTransition} from "react-transition-group";
import {IconButton} from "./IconButton";
import {Modal} from "./Modal";
import {Image} from "./Image";


function genCarouselHeight(_contWidth, _rem) {
    const _contRatio = _rem === 18 ? 0.5 : 0.666;
    return (_contWidth * _contRatio) - 2 * _rem;
}

function useElementWidth() {
    const ref = useRef(null);
    const [width, setWidth] = useState(0);

    useLayoutEffect(() => {
        const element = ref.current;
        const measure = () => setWidth(element.getBoundingClientRect().width);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    return [ref, width];
}


export const Carousel = props => {


    const [swiper, setSwiper] = useState(null);

    const [activeIndex, setActiveIndex] = useState(0);
    const [snapIndex, setSnapIndex] = useState(0);
    const [isEnd, setIsEnd] = useState(0);

    const [direction, setDirection] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalSlide, setModalSlide] = useState(0);

    const [wrapperRef, width] = useElementWidth();



    // From the xl breakpoint up, click zones split the carousel width 2 : 10 into prev / next; the modal is not available.
    // Below xl the whole carousel opens the modal; navigation is by swipe only.
    function getDirection(event) {
        if (props.windowSize.rem !== 18) {
            return "center";
        }
        const rect = event.currentTarget.getBoundingClientRect();
        const ratio = (event.clientX - rect.x) / rect.width;
        const {isBeginning, isEnd} = swiper;
        if (ratio < 2 / 12) {
            return isBeginning ? "none" : "prev";
        }
        return isEnd ? "none" : "next";
    }

    function handleClick(event) {
        if (swiper) {
            const _direction = getDirection(event);
            setDirection(_direction);
            if (_direction === "next") {
                swiper.slideNext();
            }
            if (_direction === "prev") {
                swiper.slidePrev();
            }
            if (_direction === "center") {
                // Open the fullscreen carousel at the clicked slide
                const slideEl = event.target.closest(".swiper-slide");
                const index = Array.prototype.indexOf.call(swiper.slides, slideEl);
                setModalSlide(index >= 0 ? index : swiper.activeIndex);
                setModalOpen(true);
            }
        }
    }

    function handleMove(event) {
        if (swiper) {
            setDirection(getDirection(event));
        }
    }


    return (
        <div className="Carousel-Wrapper" ref={wrapperRef}>
            <div
                className={`Carousel ${direction === "prev" ? "CursorsPrev" : ""} ${direction === "next" ? "CursorsNext" : ""} ${direction === "center" ? "CursorsCenter" : ""}`}
                onClick={handleClick}
                onMouseMove={handleMove}
            >
                <Swiper
                    onSwiper={(swiper) => {
                        setSwiper(swiper)
                    }}
                    // onReachEnd={(args) => {
                    //     console.log("reachEnd", args)
                    // }}
                    // onSlideChange={(args) => {
                    //     console.log("onSlideChange", args)
                    // }}
                    // onSnapIndexChange={(args) => {
                    //     console.log("onSnapIndexChange", args.snapIndex)
                    // }}
                    slidesPerView={'auto'}
                    slidesPerColumnFill={'row'}
                    watchSlidesVisibility
                >
                    {props.media.map((slide, index) =>
                        <SwiperSlide key={'slide_' + index}>
                            {
                                ({isActive, isVisible}) => {
                                    if (slide.type === "bild") {
                                        return (
                                            <Bild
                                                {...slide}
                                                isActive={isActive}
                                                eager={index < 3}
                                                last={index === props.media.length - 1}
                                                carouselHeight={genCarouselHeight(width, props.windowSize.rem)}
                                            />
                                        )
                                    } else if (slide.type === "video") {
                                        return (
                                            <Video
                                                {...slide}
                                                isActive={isActive}
                                                isVisible={isVisible}
                                                last={index === props.media.length - 1}
                                                carouselHeight={genCarouselHeight(width, props.windowSize.rem)}
                                            />
                                        )
                                    }
                                }
                            }
                        </SwiperSlide>
                    )}
                </Swiper>
            </div>
            <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}>
                <FullscreenCarousel
                    media={props.media}
                    initialSlide={modalSlide}
                    windowSize={props.windowSize}
                    onClose={() => setModalOpen(false)}
                />
            </Modal>
        </div>
    )
}


const Bild = props => {
    const {image, last, carouselHeight, eager} = props
    const calcedWidth = carouselHeight * (image.width / image.height);
    return (
<div
    className={`Carousel-Slide Carousel-Slide-Bild ${last ? "last" : ""} ${props.isActive ? "active" : ""}`}
    style={{
        width: calcedWidth,
    }}
>
    {calcedWidth > 0 && <Image image={image} sizes={`${Math.round(calcedWidth)}px`} loading={eager ? "eager" : "lazy"} />}
</div>
    );
}


const Video = props => {
    const {last, carouselHeight, isActive, isVisible} = props;
    const calcedWidth = carouselHeight * (props.width / props.height);

    return (
<div className={`Carousel-Slide Carousel-Slide-Video ${last ? "last" : ""} ${isActive ? "active" : ""}`}>
    <VideoPlayer
        videoId={props.videoId}
        paused={!isVisible}
        style={{
            width: calcedWidth,
        }}
    />
</div>
    );
}


const VideoPlayer = ({videoId, paused, style}) => {

    const [muted, setMuted] = useState(true)
    const [manualPaused, setManualPaused] = useState(false)


    const handleMute = (e) => {
e.stopPropagation();
setMuted(bool => !bool);
    }
    const handleManualPause = (e) => {
e.stopPropagation();

setManualPaused(bool => !bool);
    }

    return (
    <div
        className="Carousel-Slide-IframeWrapper"
        style={style}
    >

        <div className="Carousel-Slide-IframeControls">
            <IconButton
                type={manualPaused ? "play-true" : "play-false"}
                handleClick={handleManualPause}
            />
            <IconButton
                type={muted ? "mute-true" : "mute-false"}
                handleClick={handleMute}
            />
        </div>

        <Vimeo
            background={true}
            autoplay={true}
            video={videoId}
            paused={paused || manualPaused}
            muted={false}
            volume={muted ? 0 : 1}
        />

    </div>
    );
}


const FullscreenCarousel = ({media, initialSlide, windowSize, onClose}) => {

    const [swiper, setSwiper] = useState(null);
    const [direction, setDirection] = useState("none");

    // Largest size that fits the viewport minus a 1rem gutter
    const fit = (width, height) => {
        const scale = Math.min(
            (windowSize.width - 2 * windowSize.rem) / width,
            (windowSize.height - 2 * windowSize.rem) / height
        );
        return {width: width * scale, height: height * scale};
    }

    function getDirection(event) {
        const rect = event.currentTarget.getBoundingClientRect();
        const {isBeginning, isEnd} = swiper;
        if (event.clientX - rect.x < rect.width / 2) {
            return isBeginning ? "none" : "prev";
        }
        return isEnd ? "none" : "next";
    }

    function handleClick(event) {
        if (swiper) {
            const _direction = getDirection(event);
            setDirection(_direction);
            if (_direction === "next") {
                swiper.slideNext();
            }
            if (_direction === "prev") {
                swiper.slidePrev();
            }
        }
    }

    function handleMove(event) {
        if (swiper) {
            setDirection(getDirection(event));
        }
    }

    return (
        <div
            className={`FullscreenCarousel ${direction === "prev" ? "CursorsPrev" : ""} ${direction === "next" ? "CursorsNext" : ""}`}
            onClick={handleClick}
            onMouseMove={handleMove}
        >
            <Swiper
                onSwiper={setSwiper}
                modules={[Keyboard]}
                keyboard={{enabled: true}}
                initialSlide={initialSlide}
                slidesPerView={1}
            >
                {media.map((slide, index) =>
                    <SwiperSlide key={'fullscreen_slide_' + index}>
                        {
                            ({isActive}) => {
                                if (slide.type === "bild") {
                                    const size = fit(slide.image.width, slide.image.height);
                                    return (
                                        <Image
                                            image={slide.image}
                                            className="FullscreenCarousel-Bild"
                                            style={size}
                                            sizes={`${Math.round(size.width)}px`}
                                        />
                                    )
                                } else if (slide.type === "video") {
                                    return (
                                        <VideoPlayer
                                            videoId={slide.videoId}
                                            paused={!isActive}
                                            style={fit(slide.width, slide.height)}
                                        />
                                    )
                                }
                            }
                        }
                    </SwiperSlide>
                )}
            </Swiper>

            <button
                className="FullscreenCarousel-Close"
                aria-label="Close"
                onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                }}
                onMouseMove={(e) => e.stopPropagation()}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">
                    <g style={{stroke: "#00FD8D", strokeWidth: 3.5}}>
                        <line x1="2" y1="2" x2="26" y2="26"/>
                        <line x1="26" y1="2" x2="2" y2="26"/>
                    </g>
                </svg>
            </button>
        </div>
    );
}
